import { useState, useCallback, useEffect } from 'react';
import { Paper, Tooltip } from '@mui/material';
import { Spinner, Input } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  getVIRCategories,
  createVIRCategory,
  updateVIRCategory,
  deleteVIRCategory,
  deleteVIRReport,
  updateVIRReport,
} from './FormikHelpers/Api';
import { VIRReportFormModal } from './VendorInspectionReportFormModal';
import { ForwardReportModal } from './ForwardReportModal';
import { formatDateDenver } from './FormikHelpers/utils';

const STATUS_CONFIG = {
  'Open': { color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE' },
  'In Progress': { color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  'Completed': { color: '#059669', bg: '#ECFDF5', border: '#6EE7B7' },
  'Not Able to Complete': { color: '#DC2626', bg: '#FEF2F2', border: '#FECDD3' },
};

const VIR_STATUSES = ['Open', 'In Progress', 'Completed', 'Not Able to Complete'];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['Open'];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        borderRadius: 20,
        padding: '3px 10px',
        fontSize: '0.72rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const InlineStatusSelect = ({ report, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG['Open'];

  const handleChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === report.status) return;
    setLoading(true);
    try {
      await updateVIRReport(report._id, { status: newStatus });
      onStatusChange(report._id, newStatus);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <select
        value={report.status}
        onChange={handleChange}
        disabled={loading}
        onClick={(e) => e.stopPropagation()}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          background: cfg.bg,
          color: cfg.color,
          border: `1px solid ${cfg.border}`,
          borderRadius: 20,
          padding: '3px 28px 3px 10px',
          fontSize: '0.72rem',
          fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer',
          outline: 'none',
          whiteSpace: 'nowrap',
          backgroundImage: 'none',
          minWidth: 60,
          opacity: loading ? 0.7 : 1,
        }}
      >
        {VIR_STATUSES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <span
        style={{
          position: 'absolute',
          right: 9,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading ? (
          <Spinner size="sm" style={{ color: cfg.color, width: 11, height: 11 }} />
        ) : (
          <svg width="9" height="5" viewBox="0 0 9 5" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L4.5 4L8 1" stroke={cfg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
    </div>
  );
};

const VIRReportsTable = ({
  reports: initialReports,
  loading,
  properties,
  categoryOptions,
  userRole,
  onSaveReport,
  onRefresh,
  filters,
}) => {
  const [reports, setReports] = useState(initialReports);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardReport, setForwardReport] = useState(null);

  useEffect(() => { setReports(initialReports); }, [initialReports]);

  const openForward = (report, e) => {
    e.stopPropagation();
    setForwardReport(report);
    setForwardOpen(true);
  };

  const canEdit = ['company', 'manager', 'technical staff', 'vendor'].includes(
    (userRole || '').toLowerCase()
  );

  const canCreateNew = !!(filters?.propertyId && filters?.buildingName);

  const prefilledPropertyLabel = (() => {
    if (!filters?.propertyId) return '';
    const prop = (properties || []).find((p) => p._id === filters.propertyId);
    return prop ? (prop.title || prop.name || filters.propertyId) : filters.propertyId;
  })();

  const openNew = () => {
    if (!canCreateNew) {
      toast.warning('Please select a property and building before creating a new report.');
      return;
    }
    setSelectedReport(null);
    setIsNew(true);
    setModalOpen(true);
  };

  const openEdit = (report) => {
    setSelectedReport(report);
    setIsNew(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedReport(null);
    onRefresh?.();
  };

  const handleSave = async (formData, isNewReport, existingId) => {
    const result = await onSaveReport(formData, isNewReport, existingId);
    return result;
  };

  const handleStatusChange = (reportId, newStatus) => {
    setReports((prev) =>
      prev.map((r) => (r._id === reportId ? { ...r, status: newStatus } : r))
    );
  };

  const handleDelete = async (report) => {
    const res = await Swal.fire({
      title: 'Delete Report?',
      html: `<p>Delete the inspection report for <strong>${report.property?.buildingName || 'this property'}</strong>?<br/><small style="color:#64748B">This action cannot be undone.</small></p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteVIRReport(report._id);
      onRefresh?.();
    } catch {
      toast.error('Failed to delete report');
    }
  };

  const colSpan = canEdit ? 9 : 8;

  return (
    <>
      <Paper elevation={0} className="it-wrap it-paper">
        <div className="it-header">
          <div className="it-header__left">
            <div className="it-header__icon-wrap">
              <FeatherIcon icon="file-text" size={18} color="#A5B4FC" />
            </div>
            <div className="it-header__meta">
              <div className="it-header__label">Maintenance</div>
              <div className="it-header__title">Vendor Inspection Reports</div>
            </div>
            {reports.length > 0 && (
              <div className="it-header__badges">
                <span className="it-header__badge it-header__badge--records">
                  {reports.length} report{reports.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
          <div className="it-header__actions">
            {canEdit && (
              <Tooltip
                title={canCreateNew ? '' : 'Select a property and building to create a new report'}
                arrow
              >
                <span>
                  <button
                    type="button"
                    className="it-btn it-btn--primary-solid it-add"
                    onClick={openNew}
                    disabled={!canCreateNew}
                    style={{ opacity: canCreateNew ? 1 : 0.5, cursor: canCreateNew ? 'pointer' : 'not-allowed' }}
                  >
                    <FeatherIcon icon="plus" size={14} />
                    New Report
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="it-tbl" style={{ minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 80 }}>Report No.</th>
                <th style={{ minWidth: 180, textAlign: 'left', paddingLeft: 12 }}>Property / Building</th>
                <th style={{ minWidth: 140 }}>Category</th>
                <th style={{ minWidth: 130 }}>Inspection Date</th>
                <th style={{ minWidth: 140 }}>Inspector</th>
                <th style={{ minWidth: 160 }}>Status</th>
                <th style={{ minWidth: 180, textAlign: 'left', paddingLeft: 10 }}>Comments</th>
                <th style={{ minWidth: 80 }}>Files</th>
                {canEdit && <th style={{ width: 100 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={colSpan + 1} style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spinner style={{ color: '#4F46E5' }} />
                  </td>
                </tr>
              )}

              {!loading && reports.length === 0 && (
                <tr>
                  <td colSpan={colSpan + 1} className="it-empty-cell">
                    <div className="it-empty" style={{ minHeight: '25vh' }}>
                      <div className="it-empty__icon-wrap">
                        <FeatherIcon icon="file-text" size={24} color="#4F46E5" />
                      </div>
                      <div className="it-empty__title">No Inspection Reports</div>
                      <div className="it-empty__sub">
                        {canEdit
                          ? canCreateNew
                            ? 'Click "New Report" to create your first vendor inspection report.'
                            : 'Select a property and building, then click "New Report" to get started.'
                          : 'No reports found for the current filters.'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                reports.map((report) => {
                  const categoryName = report.category?.name || '—';
                  const reportFileCount = Array.isArray(report.reportFiles)
                    ? report.reportFiles.length
                    : report.reportFile ? 1 : 0;
                  const hasFiles = reportFileCount > 0 || report.attachments?.length > 0;
                  const totalFiles = reportFileCount + (report.attachments?.length || 0);
                  const inspectorName = report.inspectedBy?.displayName || '—';

                  const getPropertyLabel = (r) => {
                    if (r.property?.title || r.property?.name) return r.property.title || r.property.name;
                    const propId = r.property?.propertyId || r.propertyId;
                    if (propId) {
                      const foundProp = (properties || []).find((p) => p._id === propId);
                      if (foundProp) return foundProp.title || foundProp.name || propId;
                      return propId;
                    }
                    return '—';
                  };

                  return (
                    <tr
                      key={report._id}
                      className="it-row"
                      onClick={() => openEdit(report)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#4F46E5', fontSize: '0.82rem' }}>
                          {report.reportNo ? report.reportNo : '—'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'left', paddingLeft: 12 }}>
                        <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.82rem' }}>
                          {getPropertyLabel(report)}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: 1 }}>
                          {report.property?.buildingName || '—'}
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            background: '#EEF2FF',
                            color: '#4338CA',
                            border: '1px solid #C7D2FE',
                            borderRadius: 6,
                            padding: '3px 10px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {categoryName}
                        </span>
                      </td>
                      <td>
                        <span className="it-cell-date">{formatDateDenver(report.inspectionDate)}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 500 }}>
                          {inspectorName}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {canEdit ? (
                          <InlineStatusSelect report={report} onStatusChange={handleStatusChange} />
                        ) : (
                          <StatusBadge status={report.status} />
                        )}
                      </td>

                      <td style={{ textAlign: 'left', paddingLeft: 10, maxWidth: 180 }}>
                        <span
                          style={{
                            fontSize: '0.78rem',
                            color: '#475569',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {report.comments || <span className="it-cell-muted">—</span>}
                        </span>
                      </td>
                      <td>
                        {hasFiles ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              background: '#EEF2FF',
                              color: '#4F46E5',
                              borderRadius: 6,
                              padding: '3px 8px',
                              fontSize: '0.72rem',
                              fontWeight: 600,
                            }}
                          >
                            <FeatherIcon icon="paperclip" size={11} />
                            {totalFiles}
                          </span>
                        ) : (
                          <span className="it-cell-muted">—</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="it-actions-td" onClick={(e) => e.stopPropagation()}>
                          <div className="it-action-group">
                            <Tooltip title="Edit" arrow>
                              <button
                                type="button"
                                className="it-ebtn"
                                onClick={(e) => { e.stopPropagation(); openEdit(report); }}
                              >
                                <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                              <button
                                type="button"
                                className="it-dbtn"
                                onClick={(e) => { e.stopPropagation(); handleDelete(report); }}
                              >
                                <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Forward Report" arrow>
                              <button
                                type="button"
                                className="it-ebtn"
                                style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}
                                onClick={(e) => openForward(report, e)}
                              >
                                <FeatherIcon icon="send" size={13} color="#059669" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Paper>

      <VIRReportFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        reportData={selectedReport}
        isNew={isNew}
        properties={properties}
        categoryOptions={categoryOptions}
        userRole={userRole}
        prefilledPropertyId={filters?.propertyId || ''}
        prefilledBuildingName={filters?.buildingName || ''}
        prefilledPropertyLabel={prefilledPropertyLabel}
      />

      <ForwardReportModal
        isOpen={forwardOpen}
        onClose={() => { setForwardOpen(false); setForwardReport(null); }}
        reportId={forwardReport?._id}
        reportData={forwardReport}
      />
    </>
  );
};

const FInput = ({ value, onChange, placeholder, readOnly, type = 'text' }) => (
  <Input
    type={type}
    bsSize="sm"
    value={value || ''}
    onChange={(e) => !readOnly && onChange(e.target.value)}
    placeholder={placeholder}
    readOnly={readOnly}
    style={{
      borderColor: '#E2E8F0',
      borderRadius: 7,
      fontSize: '0.82rem',
      backgroundColor: readOnly ? '#f8f9fa' : undefined,
    }}
  />
);

const emptyCategory = { name: '', description: '' };

const InlineCategoryRow = ({ data, isNew, onSave, onCancel }) => {
  const [row, setRow] = useState({ isActive: true, ...data });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try { await onSave(row); } finally { setSaving(false); }
  };

  return (
    <tr className={`it-editable-row it-editable-row--${isNew ? 'new' : 'edit'}`}>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 40, textAlign: 'center' }}>
        <div className={`it-expand-icon it-expand-icon--${isNew ? 'new' : 'edit'}`}>
          <FeatherIcon icon={isNew ? 'plus' : 'edit-2'} size={12} color={isNew ? '#059669' : '#4F46E5'} />
        </div>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 200 }}>
        <FInput value={row.name} onChange={set('name')} placeholder="Category name..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 300 }}>
        <FInput value={row.description} onChange={set('description')} placeholder="Description (optional)..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 100, textAlign: 'center' }}>
        {isNew ? (
          <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>Active</span>
        ) : (
          <select
            value={row.isActive ? 'true' : 'false'}
            onChange={(e) => set('isActive')(e.target.value === 'true')}
            style={{
              border: '1px solid #E2E8F0', borderRadius: 7, fontSize: '0.78rem',
              padding: '4px 8px', cursor: 'pointer', outline: 'none',
              background: row.isActive ? '#ECFDF5' : '#FEF2F2',
              color: row.isActive ? '#059669' : '#DC2626',
              fontWeight: 700, width: '100%',
            }}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )}
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 130, textAlign: 'center' }}>
        <div className="it-action-group">
          <button type="button" className="it-btn it-btn--cancel" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button
            type="button"
            className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </td>
    </tr>
  );
};

const VIRCategoriesTable = ({ userRole, isLead, onRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const role = (userRole || '').toLowerCase();
  const canManage = role === 'company' || (role === 'technical staff' && isLead);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVIRCategories(showInactive ? {} : { activeOnly: 'true' });
      setCategories(res?.data || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleCreate = async (row) => {
    await createVIRCategory({ name: row.name.trim(), description: row.description?.trim() || '' });
    setShowNewRow(false);
    fetchCategories();
  };

  const handleUpdate = async (id, row) => {
    await updateVIRCategory(id, { name: row.name.trim(), description: row.description?.trim() || '', isActive: row.isActive });
    setEditingId(null);
    fetchCategories();
  };

  const handleDelete = async (cat) => {
    const res = await Swal.fire({
      title: 'Deactivate Category?',
      html: `<p>Deactivate <strong>${cat.name}</strong>? It will no longer appear in new reports.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel',
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteVIRCategory(cat._id);
      toast.success('Category deactivated');
      fetchCategories();
    } catch {
      toast.error('Failed to deactivate category');
    }
  };

  return (
    <Paper elevation={0} className="it-wrap it-paper">
      <div className="it-header">
        <div className="it-header__left">
          <div className="it-header__icon-wrap">
            <FeatherIcon icon="layers" size={18} color="#A5B4FC" />
          </div>
          <div className="it-header__meta">
            <div className="it-header__label">Configuration</div>
            <div className="it-header__title">Inspection Categories</div>
          </div>
          {categories.length > 0 && (
            <div className="it-header__badges">
              <span className="it-header__badge it-header__badge--records">
                {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}
              </span>
            </div>
          )}
        </div>
        <div className="it-header__actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {canManage && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6,
              color: 'rgba(165,180,252,0.75)', fontSize: '0.78rem',
              cursor: 'pointer', marginBottom: 0,
            }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show inactive
            </label>
          )}
          {canManage && (
            <button
              type="button"
              className={`it-btn it-add ${showNewRow ? 'it-btn--danger' : 'it-btn--primary-solid'}`}
              onClick={() => { setShowNewRow((v) => !v); setEditingId(null); }}
            >
              <FeatherIcon icon={showNewRow ? 'x' : 'plus'} size={14} />
              {showNewRow ? 'Cancel' : 'Add Category'}
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="it-tbl" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th style={{ minWidth: 200 }}>Name</th>
              <th style={{ minWidth: 300 }}>Description</th>
              <th style={{ width: 100 }}>Status</th>
              {canManage && <th style={{ width: 130 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineCategoryRow
                data={{ ...emptyCategory }}
                isNew
                onSave={handleCreate}
                onCancel={() => setShowNewRow(false)}
              />
            )}

            {loading && (
              <tr>
                <td colSpan={canManage ? 5 : 4} style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Spinner style={{ color: '#4F46E5' }} />
                </td>
              </tr>
            )}

            {!loading && categories.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="it-empty-cell">
                  <div className="it-empty" style={{ minHeight: '25vh' }}>
                    <div className="it-empty__icon-wrap">
                      <FeatherIcon icon="layers" size={24} color="#4F46E5" />
                    </div>
                    <div className="it-empty__title">No Inspection Categories</div>
                    <div className="it-empty__sub">
                      {canManage ? 'Click "Add Category" to create your first category.' : 'No categories configured yet.'}
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && categories.map((cat) => {
              if (canManage && editingId === cat._id) {
                return (
                  <InlineCategoryRow
                    key={cat._id}
                    data={cat}
                    isNew={false}
                    onSave={(row) => handleUpdate(cat._id, row)}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              return (
                <tr
                  key={cat._id}
                  className="it-row"
                  onClick={() => canManage && setEditingId(cat._id)}
                  style={{ cursor: canManage ? 'pointer' : 'default' }}
                >
                  <td className="it-expand-td">
                    {canManage && (
                      <div className="it-expand-icon it-expand-icon--view">
                        <FeatherIcon icon="edit-3" size={12} color="#4F46E5" />
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.82rem' }}>{cat.name}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.80rem', color: '#475569' }}>
                      {cat.description || <span className="it-cell-muted">—</span>}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`it-badge ${cat.isActive ? 'it-badge--ok' : 'it-badge--overdue'}`}>
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="it-actions-td" onClick={(e) => e.stopPropagation()}>
                      <div className="it-action-group">
                        <Tooltip title="Edit" arrow>
                          <button
                            type="button"
                            className="it-ebtn"
                            onClick={(e) => { e.stopPropagation(); setEditingId(cat._id); }}
                          >
                            <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Deactivate" arrow>
                          <button
                            type="button"
                            className="it-dbtn"
                            onClick={(e) => { e.stopPropagation(); handleDelete(cat); }}
                          >
                            <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { VIRReportsTable, VIRCategoriesTable };